import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import {
  Checkbox,
  Chip,
  ConfirmDialog,
  Drawer,
  FormField,
  Select,
  TextField,
} from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { AddCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline, Stack } from "components/layout/reusableLayouts";
import RemovableField from "components/common/RemovableField";
import CustomFields from "components/common/CustomFields";
import useCustomFieldEditor from "hooks/useCustomFieldEditor";
import CustomFieldParentType from "enums/CustomFieldParentType";
import CustomFieldStatus from "enums/CustomFieldStatus";
import CustomFieldType from "enums/CustomFieldType";
import {
  CustomFieldParentTypeDisplay,
  CustomFieldTypeDisplay,
  type CustomFieldsRecord,
  type FormikCustomField,
} from "hooks/customFields.types";
import { useDynamicConfirmModal } from "hooks/useDynamicConfirmModal";
import AllocationTypeDropdown, {
  AllocationTypeOption,
} from "components/common/AllocationTypeDropdown";
import { useFormikHelpers } from "hooks/useFormikHelpers";

/**
 * Wider than the default paper because this drawer is an editor with a preview
 * pane, not a detail panel. The money-movement drawers set the same precedent
 * at 544.
 */
const DRAWER_WIDTH = 640;
/** A preview and an option row read at the width they will really have. */
const FIELD_COLUMN_WIDTH = 360;

/**
 * Equal halves, not content-sized cells: a field sized to its own content puts
 * the second column at a different offset in every row and nothing lines up
 * down the form.
 */
const half = { flex: 1, minWidth: 0 } as const;

interface ApprovalsDrawerDrawerProps {
  open: boolean;
  handleCloseDrawer: (isRefetch?: boolean, skipDirty?: boolean) => void;
  selectedFieldId: string;
}

const CustomFieldSettingsDrawer: React.FC<ApprovalsDrawerDrawerProps> = (
  props,
) => {
  const { open, handleCloseDrawer, selectedFieldId } = props;
  const { t } = useTranslation();

  const { formik, isAnyLoading } = useCustomFieldEditor({
    customFieldId: selectedFieldId,
  });

  const { handleFormikError, handleFormikHelperText } =
    useFormikHelpers(formik);

  // The DS resolves a field's message as `errorText ?? helperText`, so an
  // absent message has to be undefined rather than the helper's null.
  const fieldError = (name: string, options?: { deep?: boolean }) =>
    handleFormikHelperText(name, options) ?? undefined;

  const {
    dynamicConfirmModal,
    openDynamicConfirmModal,
    closeDynamicConfirmModal,
  } = useDynamicConfirmModal();

  const handleAddOption = () => {
    const newOptions = [...(formik.values?.options || []), ""];
    formik.setFieldValue("options", newOptions);
    formik.setFieldTouched(`options[${0}]`, true, true);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = (formik.values?.options || []).filter(
      (_, i) => i !== index,
    );
    formik.setFieldValue("options", newOptions);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = (formik.values?.options || []).map((option, i) =>
      i === index ? value : option,
    );
    formik.setFieldValue("options", newOptions);
    formik.setFieldTouched(`options[${index}]`, true, true);
  };

  // Yup reports either one message for the whole array or one per entry.
  const optionError = (index: number): string | undefined => {
    if (!formik.touched.options || !formik.errors.options) return undefined;
    const errors = formik.errors.options;
    const message = typeof errors === "string" ? errors : errors[index];
    return typeof message === "string" ? message : undefined;
  };

  const toggleParentType = (parentType: CustomFieldParentType) => {
    const selected = formik.values.parentTypes.includes(parentType);
    formik.setFieldValue(
      "parentTypes",
      selected
        ? formik.values.parentTypes.filter((value) => value !== parentType)
        : [...formik.values.parentTypes, parentType],
    );
    formik.setFieldTouched("parentTypes", true, false);
  };

  const handleAllocationTypeChange = (type: AllocationTypeOption | null) => {
    const applySelection = () => handleOptionChange(0, type ? type.key : "");

    // The field this drawer is editing collects only what it started with, so anything else that
    // comes back tagged is collected by a different custom field.
    const isOwnAllocationType = type?.key === formik.initialValues.options?.[0];

    if (
      isOwnAllocationType ||
      !type?.isLinkedCustomField ||
      !type?.integrationEntity
    ) {
      applySelection();
      return;
    }

    openDynamicConfirmModal(
      t("Allocation is already linked"),
      t("AllocationTypeAlreadyLinkedDescription", {
        allocationType: type.name,
        fieldName: type.isLinkedCustomField,
      }),
      applySelection,
    );
  };

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values?.options]);

  const setCloseDrawer = (isRefetch?: boolean, skipDirty?: boolean) => {
    if (formik.dirty && !skipDirty) {
      handleDirtyPopover();
    } else {
      handleCloseDrawer(isRefetch);
      formik.resetForm();
    }
  };

  const handleDirtyPopover = () => {
    openDynamicConfirmModal(
      t("Unsaved Changes"),
      t("You have unsaved changes, are you sure you want to leave?"),
      async () => {
        setCloseDrawer(false, true);
      },
    );
  };

  const isListLike =
    formik.values.type === CustomFieldType.LIST ||
    formik.values.type === CustomFieldType.RADIO;

  return (
    <>
      <Drawer
        open={open}
        onClose={() => setCloseDrawer(false)}
        width={DRAWER_WIDTH}
        title={formik.values?.id ? t("Edit Field") : t("Create New Field")}
        loading={isAnyLoading}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={() => setCloseDrawer(false)}
            >
              {t("Exit")}
            </Button>

            <Button
              variant="primary-filled"
              disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
              onClick={async () => {
                await formik.submitForm();
                setCloseDrawer(true, true);
              }}
            >
              {t("Save")}
            </Button>
          </>
        }
      >
        <Stack gap="var(--space-8)">
          <Stack gap="var(--space-4)">
            <Typography variant="h6">{t("Field Basics")}</Typography>
            <Stack gap="var(--space-6)">
              <Inline gap="var(--space-6)" align="start" wrap={false}>
                <Box sx={half}>
                  <TextField
                    label={t("Field Name")}
                    placeholder={t("Field Name")}
                    name="label"
                    value={formik.values.label}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorText={fieldError("label")}
                    required
                    fullWidth
                  />
                </Box>

                {/* The DS Select takes no onBlur and blur bubbles, so the
                    wrapper marks it touched — otherwise a required Type never
                    shows its message and Save is disabled with no reason
                    given. Same pattern as AllocationTypeDropdown below. */}
                <Box
                  sx={half}
                  onBlur={() => formik.setFieldTouched("type", true)}
                >
                  <Select
                    label={t("Field Type")}
                    placeholder={t("Field Type")}
                    options={Object.values(CustomFieldType).map((type) => ({
                      id: type,
                      label: t(CustomFieldTypeDisplay[type]),
                    }))}
                    value={formik.values.type || null}
                    onChange={(type) => {
                      formik.setFieldValue("type", type ?? "");
                      formik.setFieldValue("options", [""]);
                    }}
                    errorText={fieldError("type")}
                    // The type decides what is already stored against the field,
                    // so it is fixed once the field exists.
                    disabled={!!formik.values.id}
                    required
                    fullWidth
                  />
                </Box>
              </Inline>

              <Inline gap="var(--space-6)" align="start" wrap={false}>
                <Box sx={half}>
                  <TextField
                    label={t("Order")}
                    tooltip={t(
                      "This is the order it will be displayed in its 'Used In' type. If two components have the same order, one will be chosen at random to appear first.",
                    )}
                    type="number"
                    placeholder={t("Order")}
                    name="order"
                    value={formik.values.order}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    errorText={fieldError("order")}
                    inputProps={{ min: 1, max: 999 }}
                    fullWidth
                  />
                </Box>

                <Box sx={half}>
                  <Select
                    label={t("Status")}
                    options={[
                      { id: CustomFieldStatus.ACTIVE, label: t("Active") },
                      { id: CustomFieldStatus.DISABLED, label: t("Disabled") },
                      { id: CustomFieldStatus.READONLY, label: t("Read Only") },
                    ]}
                    value={formik.values.status}
                    onChange={(status) =>
                      formik.setFieldValue("status", status ?? "")
                    }
                    errorText={fieldError("status")}
                    required
                    fullWidth
                  />
                </Box>
              </Inline>

              {/* Seven fixed options, so they are all on screen as toggles rather
                than behind a menu. onClick carries the whole toggle; the x is
                the same action spelled out on a chip that is already on. */}
              <FormField
                asFieldset
                label={t("Used In")}
                errorText={fieldError("parentTypes")}
                required
                fullWidth
              >
                {/* The DS fields put 8px between label and control; the
                    fieldset legend has none of its own, so the row matches it
                    by hand or Used In sits tighter than its neighbours. */}
                <Inline gap="var(--space-3)" sx={{ mt: "var(--space-3)" }}>
                  {Object.values(CustomFieldParentType).map((parentType) => {
                    const selected =
                      formik.values.parentTypes.includes(parentType);
                    return (
                      <Chip
                        key={parentType}
                        size="sm"
                        selected={selected}
                        aria-pressed={selected}
                        onClick={() => toggleParentType(parentType)}
                        onClear={
                          selected
                            ? () => toggleParentType(parentType)
                            : undefined
                        }
                      >
                        {t(CustomFieldParentTypeDisplay[parentType])}
                      </Chip>
                    );
                  })}
                </Inline>
              </FormField>

              <FormControlLabel
                control={
                  <Switch
                    name="isRequired"
                    checked={formik.values.isRequired}
                    onChange={(event) =>
                      formik.setFieldValue("isRequired", event.target.checked)
                    }
                  />
                }
                label={t("Required?")}
              />
            </Stack>
          </Stack>

          {formik.values.type && (
            <>
              <Divider />

              <Stack gap="var(--space-4)">
                {formik.values.type !== CustomFieldType.CHECKBOX && (
                  <Typography variant="h6">
                    {`${t(CustomFieldTypeDisplay[formik.values.type])} ${t(
                      "Field Options",
                    )}`}
                  </Typography>
                )}

                {isListLike && formik.values?.options && (
                  <Stack
                    gap="var(--space-4)"
                    sx={{ maxWidth: FIELD_COLUMN_WIDTH }}
                  >
                    {formik.values.options.map(
                      (option: string, index: number) => (
                        <RemovableField
                          key={index}
                          removeLabel={t("Delete")}
                          onRemove={
                            (formik.values.options?.length ?? 0) > 1
                              ? () => handleRemoveOption(index)
                              : undefined
                          }
                        >
                          <TextField
                            label={`${t("Option")} ${index + 1}`}
                            placeholder={t("Option")}
                            value={option}
                            onChange={(e) =>
                              handleOptionChange(index, e.target.value)
                            }
                            onBlur={() =>
                              formik.setFieldTouched(
                                `options[${index}]`,
                                true,
                                true,
                              )
                            }
                            errorText={optionError(index)}
                            fullWidth
                          />
                        </RemovableField>
                      ),
                    )}

                    <Inline>
                      <Button
                        variant="default-outlined"
                        startIcon={
                          <HugeiconsIcon icon={AddCircleIcon} size={16} />
                        }
                        onClick={handleAddOption}
                      >
                        {t("Add Additional Option")}
                      </Button>
                    </Inline>
                  </Stack>
                )}

                {formik.values.type === CustomFieldType.TEXT && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.allowMultipleEntries}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "allowMultipleEntries",
                            e.target.checked,
                          )
                        }
                        name="allowMultipleEntries"
                      />
                    }
                    label={t("Allow Multiple Entries? (up to 5)")}
                  />
                )}

                {formik.values.type === CustomFieldType.ALLOCATION && (
                  <Box sx={{ maxWidth: FIELD_COLUMN_WIDTH }}>
                    <AllocationTypeDropdown
                      valueKey={formik.values.options?.[0]}
                      onChange={handleAllocationTypeChange}
                      onBlur={() =>
                        formik.setFieldTouched("options[0]", true, false)
                      }
                      showAll={true}
                      required={true}
                      error={Boolean(
                        handleFormikError("options", { deep: true }),
                      )}
                      helperText={fieldError("options", { deep: true })}
                    />
                  </Box>
                )}
              </Stack>
            </>
          )}

          {/* isValid is true on a fresh form before validation runs, so the
              preview would mount with no type chosen and CustomFields would
              fall through to its TEXT branch. The old preview returned nothing
              for an empty type; this keeps that. */}
          {formik.isValid && formik.values.type && (
            <Card variant="outlined">
              <CardContent>
                <Stack gap="var(--space-4)">
                  <Typography variant="h6">{t("Preview")}</Typography>
                  <Box sx={{ maxWidth: FIELD_COLUMN_WIDTH }}>
                    <CustomFieldPreview field={formik.values} />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Drawer>
      <ConfirmDialog
        open={dynamicConfirmModal.open}
        tone="primary"
        title={dynamicConfirmModal.title}
        message={dynamicConfirmModal.description}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          dynamicConfirmModal.onConfirm();
          closeDynamicConfirmModal();
        }}
        onCancel={closeDynamicConfirmModal}
      />
    </>
  );
};

export default CustomFieldSettingsDrawer;

/** A field being authored has no id yet, and the preview still needs a key. */
const PREVIEW_ID = "preview";

/**
 * The live preview. Its own component because the draft value it collects is
 * editor state: nothing saves it, and it has to be reshaped whenever the type
 * or the multiple-entries switch changes underneath it.
 */
const CustomFieldPreview: React.FC<{ field: FormikCustomField }> = ({
  field,
}) => {
  const [values, setValues] = useState<CustomFieldsRecord>({});
  const id = field.id || PREVIEW_ID;
  const { type, allowMultipleEntries } = field;

  // A preview is always live, whatever status the field is being saved with:
  // previewing a disabled field should still show what it will look like.
  const previewField: FormikCustomField = {
    ...field,
    id,
    status: CustomFieldStatus.ACTIVE,
  };

  useEffect(() => {
    setValues({ [id]: type === CustomFieldType.CHECKBOX ? ["false"] : [""] });
  }, [id, type]);

  useEffect(() => {
    if (allowMultipleEntries) return;
    setValues((prev) => ({ ...prev, [id]: [(prev[id] ?? [])[0] ?? ""] }));
  }, [id, allowMultipleEntries]);

  return (
    <CustomFields
      fields={[previewField]}
      values={values}
      onChange={(fieldId, next) =>
        setValues((prev) => ({ ...prev, [fieldId]: next }))
      }
      variant="flat"
    />
  );
};
